import React, { LegacyRef, useMemo } from 'react';
import { Button, Combobox, Flex, Text } from '@highereducation/hen-ui';
import { QuestionProps } from '@de/components/Step/Step';
import { Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';
import { ErrorMessage } from '@de/components/ErrorMessage';
import {
  optionToComboboxOption,
  partitionSearchBarOptions,
} from '@de/utils/searchBarOptions';

const MultipleChoiceQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  maxSelection = 0,
  automaticNext = false,
  showOrderNumber = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  variant,
  isOptionsLoading = false,
  searchBarAllOptions,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepData = getStepMetadataById(id) || {};
  const selectedOptions: Option[] = (stepData.option as Option[]) || [];

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'multiple_choice',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant,
    leadFieldAssociated,
  });

  const hasSearchBar = Boolean(searchBarAllOptions?.length);

  const { displayedOptions, searchableOptions } = useMemo(() => {
    if (!hasSearchBar) {
      return { displayedOptions: options, searchableOptions: [] as Option[] };
    }
    return partitionSearchBarOptions(
      options,
      searchBarAllOptions as Option[],
      selectedOptions,
    );
  }, [hasSearchBar, options, searchBarAllOptions, selectedOptions]);

  const handleOptionClick = (clickedOption: Option) => {
    const isSelected = selectedOptions.find(
      (opt) => opt.optionId === clickedOption.optionId,
    );

    let newSelectedOptions: Option[];

    if (isSelected) {
      newSelectedOptions = selectedOptions.filter(
        (opt) => opt.optionId !== clickedOption.optionId,
      );
    } else {
      if (maxSelection > 0 && selectedOptions.length >= maxSelection) {
        return;
      }
      newSelectedOptions = [...selectedOptions, clickedOption];
    }

    triggerFieldInputted(
      newSelectedOptions.map((opt) => opt.text as string).join(','),
    );
    updateFlowState(id, { option: newSelectedOptions, leadFieldAssociated });
    if (
      automaticNext &&
      !isMultiQuestionChild &&
      newSelectedOptions.length === maxSelection
    ) {
      handleStepForward();
    }
  };

  const handleSearchSelect = (value: string) => {
    const option = (searchBarAllOptions ?? []).find(
      (opt) => opt.optionId === value,
    );
    if (option) {
      handleOptionClick(option);
    }
  };

  const getSelectionNumber = (option: Option): number | null => {
    const index = selectedOptions.findIndex(
      (opt) => opt.optionId === option.optionId,
    );
    return index !== -1 ? index + 1 : null;
  };

  const showLoading = isOptionsLoading && !dynamicOptionsError;

  return (
    <>
      {dynamicOptionsError ? (
        <ErrorMessage
          message={dynamicOptionsError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      <Flex
        gap='sm'
        wrap='wrap'
        align='center'
        justify='start'
        direction='row'
        className='w-full'
        ref={visibilityRef as LegacyRef<HTMLDivElement>}
      >
        {showLoading ? (
          <OptionsLoadingPlaceholder />
        ) : (
          displayedOptions.map((option) => {
            const isSelected = selectedOptions.some(
              (opt) => opt.optionId === option.optionId,
            );
            const selectionNumber = getSelectionNumber(option);

            const shouldShowOrderNumer = showOrderNumber && isSelected;

            return (
              <Button
                key={option.optionId}
                size='sm'
                type='button'
                className='w-fit'
                variant='grayOutline'
                aria-pressed={isSelected}
                onClick={() => handleOptionClick(option)}
              >
                {shouldShowOrderNumer && (
                  <Text tag='span' variant='b5' weight='semibold'>
                    {selectionNumber}.
                  </Text>
                )}
                {option.text}
              </Button>
            );
          })
        )}
      </Flex>
      {!showLoading && hasSearchBar && searchableOptions.length > 0 && (
        <Combobox
          className='mt-4'
          aria-label={title || 'Search options'}
          options={searchableOptions.map(optionToComboboxOption)}
          selectedValues={selectedOptions.map((opt) => opt.optionId)}
          maxSelectable={maxSelection}
          onSelect={handleSearchSelect}
        />
      )}
    </>
  );
};

export default MultipleChoiceQuestion;
